import { useBandContextOrNull, useCapability, useIsPro } from "../../app/bandContext.js";
import { usePublicProfile, usePersonTravel, usePersonPayment, usePersonBands, usePersonOrgs } from "../../app/hooks/useProfile.js";
import { useAvatarUrl } from "../../app/hooks/useAvatar.js";
import { useFriendBandIds } from "../../app/hooks/useFriends.js";
import { useBandTeamOrNull } from "../../app/hooks/useBandTeam.js";
import { useMessageable } from "../../app/hooks/useInbox.js";
import { useMe } from "../../app/hooks/useSession.js";
import { hasInbox } from "../../app/auth/session.js";
import { PRODUCTION_OFFICE } from "./crewParty.js";

/** What the opening surface already knows, so a hero can paint before the reads land — and so it still reads
 *  right for a person with no seat here, where there is nothing to resolve. A resolved seat WINS. */
export interface PersonProfileFallback {
  name?: string | null;
  role?: string | null;
  isOwner?: boolean;
}

/** The band the viewer navigated FROM (a band-profile browse hands it in), used as the hero backdrop when the
 *  person has no picture — the shared-bands list is empty for a friend band's member by design. */
export interface PersonProfileBackdrop {
  bandId: string;
  avatarMediaId: string | null;
}

/**
 * The ONE resolution of a person profile — who this is, what the viewer may SEE, and what the viewer may DO —
 * shared by the desktop `CrewProfileModal` and the mobile `MobileContactProfileScreen` (ART-194). Extracting
 * it is what keeps "one person dialog" true across two chromes: the permissions, the hero-backdrop chain and
 * the message gate are computed HERE once, so a second surface cannot derive them slightly differently. Both
 * consumers keep their own UI state (editing / confirming / browsing) and their own layout; only the facts
 * live here.
 *
 * Every reach for the band context is the OrNull / context-safe variant, because this resolves under the
 * roster and org shells too, where no band is mounted (see CrewProfileModal's header for the full rationale).
 */
export function usePersonProfile(
  personId: string,
  fallback?: PersonProfileFallback,
  bandBackdrop?: PersonProfileBackdrop,
) {
  const profile = usePublicProfile(personId);
  const travel = usePersonTravel(personId);
  const payment = usePersonPayment(personId);
  const bands = usePersonBands(personId);
  const orgs = usePersonOrgs(personId);
  const activeBandId = useBandContextOrNull()?.bandId;

  const me = useMe();
  const team = useBandTeamOrNull();
  const seats = team.items;
  const seat = seats.find((s) => s.personId === personId);
  const myPersonId = me.data?.person?.personId;
  const isSelf = !!myPersonId && myPersonId === personId;

  const profileName = [profile.data?.firstName, profile.data?.lastName].filter(Boolean).join(" ").trim();
  const name = seat?.displayName || fallback?.name || profileName || seat?.email || "Unnamed member";
  const role = seat?.role ?? fallback?.role ?? null;
  const titleKey = seat?.titleKey ?? null;
  const isOwner = seat?.isOwner ?? fallback?.isOwner ?? false;

  const friendBandIds = useFriendBandIds();

  // Removing a member stays AV-only — that is membership, not a position on this run.
  const canManage = useCapability("band:manage");
  // WHO THIS PERSON IS REACHABLE BY — the same person-scoped picker read the inbox's "+ New" uses, and the
  // same predicate the create gate enforces. Two conditions, both server-answered: they must be reachable AND
  // WE must be an account that can hold a conversation (`hasInbox` is false for an artist/owner account and
  // for label/booking seats). `useMessageable` alone would usually return empty for such a caller, but the
  // door and this button reading one predicate is the standing rule.
  const network = useMessageable();
  const canMessage = hasInbox(me.data) && (network.data?.people ?? []).some((x) => x.personId === personId);
  // Pro is a fact about a BAND — useIsPro answers false under the roster/org shells, which is right.
  const isPro = useIsPro();

  // TM or AV. `band:manage` is the AV-owner seat ALONE, so the TM half comes from the VIEWER's own seat.
  const canEditRoles =
    canManage || seats.some((s) => s.personId === myPersonId && s.role === "tour_manager");

  // Every seat control needs a seat to act on. No seat — a friend band's member, or any person seen from a
  // shell with no band — is not a permission failure; the controls are simply absent. The owner seat is
  // untouchable and you cannot change your own seat role (both are gate guards).
  const seatId = seat?.seatId ?? null;
  const canEditSeatRole = canEditRoles && !!seatId && !isOwner && !isSelf;
  const canEditActing = canEditRoles && !!seatId;
  // Who may MOVE a party is a DIFFERENT population: Production Office (by resolved party) or an AV seat.
  const myParty = seats.find((s) => s.personId === myPersonId)?.party ?? null;
  const canEditParty = (canManage || myParty === PRODUCTION_OFFICE) && !!seatId;
  const removable = !!seatId && !isOwner && !isSelf && canManage;

  // The person's own picture rides the authenticated avatar route; avatarUrl stays the future CDN slot.
  const avatarUrl = useAvatarUrl("person", personId, profile.data?.avatarMediaId) ?? profile.data?.avatarUrl ?? null;

  // The hero backdrop chain: the person's own photo; failing that, a blurred render of a band's main photo
  // (the one the viewer navigated from, else the person's first shared band); failing that, plain ink. The
  // band photo rides the ONE band-photo path (tenant-less global avatar route), which serves reachable and
  // unreachable bands alike. The band fetch is skipped (both args null) when the person has their own photo.
  const heroBand = bands.data?.[0];
  const backdropBandId = bandBackdrop?.bandId ?? heroBand?.bandId ?? null;
  const backdropMediaId = bandBackdrop?.avatarMediaId ?? heroBand?.avatarMediaId ?? null;
  const bandBackdropUrl = useAvatarUrl("band", avatarUrl ? null : backdropBandId, avatarUrl ? null : backdropMediaId);
  const heroUrl = avatarUrl ?? bandBackdropUrl ?? null;
  const heroIsBandBackdrop = !avatarUrl && Boolean(bandBackdropUrl);

  const orgItems = orgs.data ?? [];
  const bandItems = bands.data ?? [];
  // Two tile strips of the same shape need saying which is which; a single strip never did — so the labels
  // appear exactly when there is something to distinguish.
  const labelStrips = orgItems.length > 0 && bandItems.length > 0;

  return {
    profile, travel, payment, bands, orgs, activeBandId,
    seat, isSelf, name, role, titleKey, isOwner, friendBandIds,
    canManage, canMessage, isPro, canEditRoles,
    seatId, canEditSeatRole, canEditActing, canEditParty, removable,
    avatarUrl, heroUrl, heroIsBandBackdrop,
    orgItems, bandItems, labelStrips,
  };
}
